import {
  isPhysicallyActive,
  sharedActivityFrequencyStep,
  sharedProteinIntakeStep,
  sharedSleepQualityStep,
  sharedStressLevelStep,
  sharedWaterIntakeStep,
} from '@/flow/shared/commonQuestions'
import { validatePeso, validateTextoLivre } from '@/flow/validators'
import type { Step } from '@/types'

// Helper to check user sex
const isFemale = (ctx: { vars: Record<string, any> }) => ctx.vars.sexo === 'feminino'

// Generic helpers
const isYes = (key: string) => (ctx: { vars: Record<string, any> }) => ctx.vars[key] === 'sim'
const hasSelection = (key: string, value: string) => (ctx: { vars: Record<string, any> }) => {
  const selections = ctx.vars[key]
  return Array.isArray(selections) && selections.includes(value)
}
const hasCompulsao = (ctx: { vars: Record<string, any> }) =>
  ['compulsao', 'tratamento'].includes(ctx.vars.ansiedade_compulsao)
const isActive = isPhysicallyActive
const isConstipado = (ctx: { vars: Record<string, any> }) =>
  ['1_2_semana', 'menos_1_semana'].includes(ctx.vars.frequencia_banheiro)

/** Objective-specific steps: Weight Loss */
export const weightLossSteps: Step[] = [
  // ========== INTRO ==========
  {
    id: 'intro_weight_loss',
    type: 'message',
    content:
      'Vamos entender seu histórico e seus hábitos para montar um protocolo de emagrecimento seguro e eficaz.',
  },

  // ========== GOALS ==========
  {
    id: 'weight_loss_goals',
    type: 'choice',
    inputKey: 'objetivos_emagrecimento',
    multiple: true,
    minSelections: 1,
    content: 'Quais são seus principais objetivos com o emagrecimento?',
    options: [
      { label: 'Reduzir peso na balança', value: 'reduzir_peso' },
      { label: 'Reduzir medidas', value: 'reduzir_medidas' },
      { label: 'Diminuir gordura abdominal', value: 'gordura_abdominal' },
      { label: 'Controlar o apetite', value: 'controlar_apetite' },
      { label: 'Melhorar saúde metabólica (glicose/colesterol)', value: 'saude_metabolica' },
      { label: 'Aumentar disposição e energia', value: 'mais_energia' },
      { label: 'Melhorar autoestima', value: 'autoestima' },
      { label: 'Compulsão por doces', value: 'compulsao_doces' },
    ],
  },
  {
    id: 'has_target_weight',
    type: 'choice',
    inputKey: 'tem_meta_peso',
    content: 'Você tem um peso alvo em mente?',
    options: [
      { label: 'Sim', value: 'sim' },
      { label: 'Não', value: 'nao' },
    ],
  },
  {
    id: 'target_weight',
    type: 'input',
    inputKey: 'peso_desejado',
    content: 'Qual peso você gostaria de atingir? (em kg)',
    condition: isYes('tem_meta_peso'),
    validate: validatePeso,
  },
  {
    id: 'goal_timeline',
    type: 'choice',
    inputKey: 'prazo_meta',
    content: 'Em quanto tempo você gostaria de atingir esse objetivo?',
    allowSkip: true,
    skipLabel: 'Sem prazo definido',
    options: [
      { label: '1 a 3 meses', value: '1_3_meses' },
      { label: '3 a 6 meses', value: '3_6_meses' },
      { label: '6 a 12 meses', value: '6_12_meses' },
      { label: 'Mais de 12 meses', value: 'mais_12_meses' },
    ],
  },

  // ========== BODY COMPOSITION ==========
  {
    id: 'intro_body',
    type: 'message',
    content: 'Agora, sobre o seu corpo e onde você sente mais dificuldade.',
  },
  {
    id: 'localized_fat',
    type: 'choice',
    inputKey: 'gordura_localizada',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Não tenho área específica',
    content: 'Em quais regiões você percebe mais acúmulo de gordura?',
    options: [
      { label: 'Abdômen', value: 'abdomen' },
      { label: 'Flancos / Cintura', value: 'flancos' },
      { label: 'Costas', value: 'costas' },
      { label: 'Quadril', value: 'quadril' },
      { label: 'Culote', value: 'culote' },
      { label: 'Coxas', value: 'coxas' },
      { label: 'Glúteos', value: 'gluteos' },
      { label: 'Braços', value: 'bracos' },
      { label: 'Papada / Rosto', value: 'papada' },
    ],
  },
  {
    id: 'abdomen_type',
    type: 'choice',
    inputKey: 'abdomen_preocupacao',
    content: 'Na região abdominal, você sente mais:',
    condition: hasSelection('gordura_localizada', 'abdomen'),
    options: [
      { label: 'Gordura localizada', value: 'gordura' },
      { label: 'Inchaço / Distensão', value: 'inchaco' },
      { label: 'Ambos', value: 'ambos' },
    ],
  },
  {
    id: 'fluid_retention',
    type: 'choice',
    inputKey: 'retencao_liquidos',
    content: 'Você percebe retenção de líquidos ou inchaço ao longo do dia?',
    options: [
      { label: 'Sim, frequentemente', value: 'frequente' },
      { label: 'Às vezes', value: 'as_vezes' },
      { label: 'Não', value: 'nao' },
    ],
  },

  // ========== WEIGHT HISTORY ==========
  {
    id: 'intro_history',
    type: 'message',
    content: 'Vamos falar sobre o seu histórico de peso.',
  },
  {
    id: 'weight_gain_period_female',
    type: 'choice',
    inputKey: 'inicio_ganho_peso',
    content: 'Quando você começou a ganhar peso ou percebeu piora?',
    condition: isFemale,
    options: [
      { label: 'Sempre tive tendência a ganhar peso', value: 'sempre' },
      { label: 'Após gestação', value: 'gestacao' },
      { label: 'Após alterações hormonais/anticoncepcional', value: 'hormonal' },
      { label: 'Pós-menopausa', value: 'pos_menopausa' },
      { label: 'Após os 30 anos', value: 'apos_30' },
      { label: 'Nos últimos 1-2 anos', value: 'ultimos_anos' },
      { label: 'Durante a pandemia', value: 'pandemia' },
      { label: 'Não sei ao certo', value: 'nao_sei' },
    ],
  },
  {
    id: 'weight_gain_period_other',
    type: 'choice',
    inputKey: 'inicio_ganho_peso',
    content: 'Quando você começou a ganhar peso ou percebeu piora?',
    condition: (ctx) => !isFemale(ctx),
    options: [
      { label: 'Sempre tive tendência a ganhar peso', value: 'sempre' },
      { label: 'Após os 30 anos', value: 'apos_30' },
      { label: 'Nos últimos 1-2 anos', value: 'ultimos_anos' },
      { label: 'Após parar atividade física', value: 'parou_atividade' },
      { label: 'Mudança de rotina/trabalho', value: 'mudanca_rotina' },
      { label: 'Durante a pandemia', value: 'pandemia' },
      { label: 'Não sei ao certo', value: 'nao_sei' },
    ],
  },
  {
    id: 'weight_fluctuation',
    type: 'choice',
    inputKey: 'oscilacao_peso',
    content: 'Seu peso costuma oscilar muito?',
    options: [
      { label: 'Sim, vario mais de 10 kg', value: 'mais_10kg' },
      { label: 'Sim, vario entre 5 e 10 kg', value: '5_10kg' },
      { label: 'Pouca oscilação', value: 'pouca' },
      { label: 'Meu peso é estável', value: 'estavel' },
    ],
  },
  {
    id: 'has_diet_history',
    type: 'choice',
    inputKey: 'ja_fez_dieta',
    content: 'Você já fez alguma dieta ou protocolo de emagrecimento?',
    options: [
      { label: 'Sim', value: 'sim' },
      { label: 'Não', value: 'nao' },
    ],
  },
  {
    id: 'past_diets',
    type: 'choice',
    inputKey: 'dietas_anteriores',
    multiple: true,
    minSelections: 1,
    content: 'Quais estratégias você já tentou?',
    condition: isYes('ja_fez_dieta'),
    options: [
      { label: 'Reeducação alimentar', value: 'reeducacao' },
      { label: 'Low carb', value: 'low_carb' },
      { label: 'Cetogênica', value: 'cetogenica' },
      { label: 'Jejum intermitente', value: 'jejum' },
      { label: 'Contagem de calorias/app', value: 'calorias' },
      { label: 'Substituição de refeições/shakes', value: 'shakes' },
      { label: 'Acompanhamento profissional', value: 'profissional' },
      { label: 'Medicação para emagrecer', value: 'medicacao' },
    ],
  },
  {
    id: 'diet_results',
    type: 'choice',
    inputKey: 'resultado_dietas',
    content: 'Qual foi o resultado dessas tentativas?',
    condition: isYes('ja_fez_dieta'),
    options: [
      { label: 'Perdi peso e mantive', value: 'perdi_mantive' },
      { label: 'Perdi peso, mas recuperei', value: 'perdi_recuperei' },
      { label: 'Perdi pouco', value: 'perdi_pouco' },
      { label: 'Não consegui perder', value: 'nao_perdi' },
    ],
  },
  {
    id: 'current_diet',
    type: 'choice',
    inputKey: 'segue_dieta_atual',
    content: 'Você segue alguma dieta atualmente?',
    options: [
      { label: 'Sim', value: 'sim' },
      { label: 'Não', value: 'nao' },
    ],
  },
  {
    id: 'current_diet_type',
    type: 'choice',
    inputKey: 'dieta_atual_tipo',
    multiple: true,
    minSelections: 1,
    content: 'Qual dieta ou estratégia você segue hoje?',
    condition: isYes('segue_dieta_atual'),
    options: [
      { label: 'Low carb', value: 'low_carb' },
      { label: 'Cetogênica', value: 'cetogenica' },
      { label: 'Jejum intermitente', value: 'jejum' },
      { label: 'Dieta mediterrânea', value: 'mediterranea' },
      { label: 'Contagem de calorias/app', value: 'calorias' },
      { label: 'Vegetariana/vegana', value: 'vegetariana' },
      { label: 'Acompanhamento com nutricionista', value: 'nutricionista' },
      { label: 'Outra', value: 'outros' },
    ],
  },
  {
    id: 'current_diet_other',
    type: 'input',
    inputKey: 'dieta_atual_outros',
    content: 'Qual dieta ou estratégia é essa?',
    condition: (ctx) =>
      isYes('segue_dieta_atual')(ctx) && hasSelection('dieta_atual_tipo', 'outros')(ctx),
    validate: validateTextoLivre,
  },
  {
    id: 'current_diet_duration',
    type: 'choice',
    inputKey: 'tempo_dieta_atual',
    content: 'Há quanto tempo você segue essa dieta?',
    condition: isYes('segue_dieta_atual'),
    options: [
      { label: 'Menos de 1 mês', value: 'menos_1_mes' },
      { label: '1 a 3 meses', value: '1_3_meses' },
      { label: '3 a 6 meses', value: '3_6_meses' },
      { label: '6 meses a 1 ano', value: '6_12_meses' },
      { label: 'Mais de 1 ano', value: 'mais_1_ano' },
    ],
  },

  // ========== APPETITE & BEHAVIOR ==========
  {
    id: 'intro_appetite',
    type: 'message',
    content: 'Agora, sobre sua fome, saciedade e comportamento alimentar.',
  },
  {
    id: 'satiety_time',
    type: 'choice',
    inputKey: 'tempo_saciedade',
    content: 'Depois de uma refeição completa, quanto tempo você fica saciado?',
    options: [
      { label: 'Menos de 1 hora', value: 'menos_1h' },
      { label: '1 a 2 horas', value: '1_2h' },
      { label: '2 a 3 horas', value: '2_3h' },
      { label: 'Mais de 3 horas', value: 'mais_3h' },
      { label: 'Varia muito', value: 'varia' },
    ],
  },
  {
    id: 'sweet_craving_night',
    type: 'choice',
    inputKey: 'vontade_doce_noite',
    content: 'Você sente muita vontade de comer doce à noite?',
    options: [
      { label: 'Sim', value: 'sim' },
      { label: 'Não', value: 'nao' },
    ],
  },
  {
    id: 'sweet_craving_frequency',
    type: 'choice',
    inputKey: 'frequencia_doce_noite',
    content: 'Com que frequência isso acontece?',
    condition: isYes('vontade_doce_noite'),
    options: [
      { label: '1-2 vezes por semana', value: '1_2_semana' },
      { label: '3-4 vezes por semana', value: '3_4_semana' },
      { label: 'Quase todos os dias', value: 'quase_todo_dia' },
    ],
  },
  {
    id: 'anxiety_compulsion',
    type: 'choice',
    inputKey: 'ansiedade_compulsao',
    content: 'Você sente ansiedade ou episódios de compulsão alimentar?',
    options: [
      { label: 'Não', value: 'nao' },
      { label: 'Ansiedade sem compulsão', value: 'ansiedade' },
      { label: 'Tenho episódios de compulsão', value: 'compulsao' },
      { label: 'Já tive e estou em tratamento', value: 'tratamento' },
    ],
  },
  {
    id: 'compulsion_frequency',
    type: 'choice',
    inputKey: 'frequencia_compulsao',
    content: 'Com que frequência ocorrem esses episódios?',
    condition: hasCompulsao,
    options: [
      { label: '1-2 vezes por mês', value: '1_2_mes' },
      { label: '1 vez por semana', value: '1_semana' },
      { label: 'Várias vezes por semana', value: 'varias_semana' },
      { label: 'Quase todos os dias', value: 'quase_diario' },
    ],
  },
  {
    id: 'compulsion_triggers',
    type: 'choice',
    inputKey: 'gatilhos_compulsao',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Não sei ao certo',
    content: 'O que costuma disparar esses episódios?',
    condition: hasCompulsao,
    options: [
      { label: 'Estresse', value: 'estresse' },
      { label: 'Ansiedade', value: 'ansiedade' },
      { label: 'Tristeza', value: 'tristeza' },
      { label: 'Tédio', value: 'tedio' },
      { label: 'Eventos sociais', value: 'social' },
      { label: 'Álcool', value: 'alcool' },
      { label: 'Noite / madrugada', value: 'noite' },
    ],
  },
  {
    id: 'snacking_frequency',
    type: 'choice',
    inputKey: 'beliscar_frequencia',
    content: 'Você costuma beliscar entre as refeições?',
    options: [
      { label: 'Quase sempre', value: 'quase_sempre' },
      { label: 'Às vezes', value: 'as_vezes' },
      { label: 'Raramente', value: 'raramente' },
      { label: 'Nunca', value: 'nunca' },
    ],
  },
  {
    id: 'eat_until_full',
    type: 'choice',
    inputKey: 'come_ate_cheio',
    content: 'Com que frequência você come até ficar muito cheio?',
    options: [
      { label: 'Frequentemente', value: 'frequente' },
      { label: 'Às vezes', value: 'as_vezes' },
      { label: 'Raramente', value: 'raramente' },
      { label: 'Nunca', value: 'nunca' },
    ],
  },
  {
    id: 'meals_per_day',
    type: 'choice',
    inputKey: 'numero_refeicoes',
    content: 'Em média, quantas refeições você faz por dia?',
    options: [
      { label: '1-2 refeições', value: '1_2' },
      { label: '3 refeições', value: '3' },
      { label: '4-5 refeições', value: '4_5' },
      { label: '6 ou mais', value: '6_mais' },
    ],
  },
  {
    id: 'skip_meals',
    type: 'choice',
    inputKey: 'pula_refeicoes',
    content: 'Você costuma pular alguma refeição?',
    options: [
      { label: 'Não, faço todas', value: 'nao' },
      { label: 'Pulo o café da manhã', value: 'cafe' },
      { label: 'Pulo o almoço', value: 'almoco' },
      { label: 'Pulo o jantar', value: 'jantar' },
      { label: 'Varia', value: 'varia' },
    ],
  },

  // ========== FOOD HABITS ==========
  {
    id: 'intro_food',
    type: 'message',
    content: 'Agora, alguns hábitos alimentares do dia a dia.',
  },
  {
    id: 'fruit_intake',
    type: 'choice',
    inputKey: 'consumo_frutas',
    content: 'Com que frequência você consome frutas?',
    options: [
      { label: 'Todos os dias', value: 'diario' },
      { label: '3-5 vezes por semana', value: '3_5_semana' },
      { label: '1-2 vezes por semana', value: '1_2_semana' },
      { label: 'Raramente', value: 'raramente' },
    ],
  },
  {
    id: 'vegetable_intake',
    type: 'choice',
    inputKey: 'consumo_legumes',
    content: 'Com que frequência você consome legumes?',
    options: [
      { label: 'Todos os dias', value: 'diario' },
      { label: '3-5 vezes por semana', value: '3_5_semana' },
      { label: '1-2 vezes por semana', value: '1_2_semana' },
      { label: 'Raramente', value: 'raramente' },
    ],
  },
  {
    id: 'greens_intake',
    type: 'choice',
    inputKey: 'consumo_verduras',
    content: 'Com que frequência você consome verduras?',
    options: [
      { label: 'Todos os dias', value: 'diario' },
      { label: '3-5 vezes por semana', value: '3_5_semana' },
      { label: '1-2 vezes por semana', value: '1_2_semana' },
      { label: 'Raramente', value: 'raramente' },
    ],
  },
  { ...sharedProteinIntakeStep },
  {
    id: 'is_vegetarian',
    type: 'choice',
    inputKey: 'vegetariano',
    content: 'Você é vegetariano(a) ou vegano(a)?',
    options: [
      { label: 'Sim', value: 'sim' },
      { label: 'Não', value: 'nao' },
    ],
  },
  {
    id: 'vegetarian_type',
    type: 'choice',
    inputKey: 'tipo_vegetariano',
    content: 'Qual tipo de alimentação vegetariana?',
    condition: isYes('vegetariano'),
    options: [
      { label: 'Vegetariano (consome ovos/leite)', value: 'ovo_lacto' },
      { label: 'Vegano (sem derivados animais)', value: 'vegano' },
      { label: 'Pescetariano', value: 'pescetariano' },
    ],
  },
  {
    id: 'fast_food_habit',
    type: 'choice',
    inputKey: 'fast_food',
    content: 'Com que frequência você consome fast food?',
    options: [
      { label: 'Raramente ou nunca', value: 'raro' },
      { label: '1 vez por semana', value: '1_semana' },
      { label: '2-3 vezes por semana', value: '2_3_semana' },
      { label: '4 ou mais vezes por semana', value: '4_mais' },
    ],
  },
  {
    id: 'sweets_frequency',
    type: 'choice',
    inputKey: 'doces_frequencia',
    content: 'Qual a frequência de doces no seu dia a dia?',
    options: [
      { label: 'Diariamente', value: 'diario' },
      { label: '3-4 vezes por semana', value: '3_4_semana' },
      { label: '1-2 vezes por semana', value: '1_2_semana' },
      { label: 'Raramente', value: 'raro' },
    ],
  },
  {
    id: 'sugary_drinks',
    type: 'choice',
    inputKey: 'bebidas_acucaradas',
    content: 'Você consome refrigerantes ou bebidas açucaradas?',
    options: [
      { label: 'Sim, diariamente', value: 'diario' },
      { label: 'Algumas vezes por semana', value: 'algumas_vezes' },
      { label: 'Raramente', value: 'raramente' },
      { label: 'Nunca', value: 'nunca' },
    ],
  },
  {
    id: 'alcohol_frequency',
    type: 'choice',
    inputKey: 'alcool_frequencia',
    content: 'Com que frequência você consome álcool?',
    options: [
      { label: 'Não bebo', value: 'nao_bebo' },
      { label: '1-2 vezes por semana', value: '1_2_semana' },
      { label: '3-4 vezes por semana', value: '3_4_semana' },
      { label: 'Quase todos os dias', value: 'quase_diario' },
    ],
  },
  { ...sharedWaterIntakeStep },

  // ========== DIGESTION ==========
  {
    id: 'intro_digestion',
    type: 'message',
    content: 'E sobre o funcionamento do intestino?',
  },
  {
    id: 'bathroom_frequency',
    type: 'choice',
    inputKey: 'frequencia_banheiro',
    content: 'Quantas vezes você costuma ir ao banheiro (evacuar)?',
    options: [
      { label: 'Mais de 1 vez por dia', value: 'mais_1_dia' },
      { label: '1 vez por dia', value: '1_dia' },
      { label: '3-4 vezes por semana', value: '3_4_semana' },
      { label: '1-2 vezes por semana', value: '1_2_semana' },
      { label: 'Menos de 1 vez por semana', value: 'menos_1_semana' },
    ],
  },
  {
    id: 'constipation',
    type: 'choice',
    inputKey: 'constipacao',
    content: 'Você sente constipação (intestino preso)?',
    condition: isConstipado,
    options: [
      { label: 'Sim', value: 'sim' },
      { label: 'Às vezes', value: 'as_vezes' },
      { label: 'Não', value: 'nao' },
    ],
  },
  {
    id: 'bloating',
    type: 'choice',
    inputKey: 'inchaco_gases',
    content: 'Você sente inchaço, gases ou desconforto após as refeições?',
    options: [
      { label: 'Sim, frequentemente', value: 'frequente' },
      { label: 'Às vezes', value: 'as_vezes' },
      { label: 'Não', value: 'nao' },
    ],
  },

  // ========== ACTIVITY ==========
  {
    id: 'intro_activity',
    type: 'message',
    content: 'Agora, vamos falar sobre atividade física e rotina.',
  },
  { ...sharedActivityFrequencyStep },
  {
    id: 'activity_type',
    type: 'choice',
    inputKey: 'tipo_atividade',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Não sei definir',
    content: 'Quais tipos de atividade você faz?',
    condition: isActive,
    options: [
      { label: 'Musculação', value: 'musculacao' },
      { label: 'Caminhada', value: 'caminhada' },
      { label: 'Corrida', value: 'corrida' },
      { label: 'Funcional / HIIT', value: 'hiit' },
      { label: 'Artes Marciais', value: 'artes_marciais' },
      { label: 'Natação', value: 'natacao' },
      { label: 'Esportes', value: 'esportes' },
      { label: 'Pilates / Yoga', value: 'pilates_yoga' },
      { label: 'Dança', value: 'danca' },
    ],
  },
  {
    id: 'activity_duration',
    type: 'choice',
    inputKey: 'duracao_atividade',
    content: 'Em média, quanto tempo dura cada treino?',
    condition: isActive,
    options: [
      { label: 'Menos de 30 minutos', value: 'menos_30' },
      { label: '30 a 45 minutos', value: '30_45' },
      { label: '45 a 60 minutos', value: '45_60' },
      { label: 'Mais de 60 minutos', value: 'mais_60' },
    ],
  },
  {
    id: 'sedentary_time',
    type: 'choice',
    inputKey: 'tempo_sedentario',
    content: 'Como é sua rotina de movimento no dia a dia?',
    options: [
      { label: 'Fico a maior parte do tempo sentado(a)', value: 'muito_sedentario' },
      { label: 'Me movimento moderadamente', value: 'moderado' },
      { label: 'Me movimento bastante', value: 'ativo' },
    ],
  },

  // ========== SLEEP & STRESS ==========
  {
    id: 'intro_sleep_stress',
    type: 'message',
    content: 'Sono e estresse também influenciam o emagrecimento.',
  },
  {
    id: 'sleep_hours',
    type: 'choice',
    inputKey: 'horas_sono',
    content: 'Quantas horas você dorme por noite?',
    options: [
      { label: 'Menos de 5 horas', value: 'menos_5' },
      { label: '5 a 6 horas', value: '5_6' },
      { label: '7 a 8 horas', value: '7_8' },
      { label: 'Mais de 8 horas', value: 'mais_8' },
    ],
  },
  { ...sharedSleepQualityStep },
  { ...sharedStressLevelStep },

  // ========== HEALTH & HORMONES ==========
  {
    id: 'intro_health',
    type: 'message',
    content: 'Agora algumas perguntas sobre saúde e histórico clínico.',
  },
  {
    id: 'clinical_conditions',
    type: 'choice',
    inputKey: 'condicoes_clinicas',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Nenhuma',
    content: 'Você tem alguma dessas condições?',
    options: [
      { label: 'Hipertensão', value: 'hipertensao' },
      { label: 'Diabetes', value: 'diabetes' },
      { label: 'Resistência à insulina', value: 'resistencia_insulina' },
      { label: 'Colesterol ou triglicérides altos', value: 'dislipidemia' },
      { label: 'Esteatose hepática', value: 'esteatose' },
      { label: 'Apneia do sono', value: 'apneia' },
      { label: 'Dores articulares', value: 'dores_articulares' },
      { label: 'Ansiedade ou depressão', value: 'saude_mental' },
      { label: 'Outras', value: 'outros' },
    ],
  },
  {
    id: 'clinical_conditions_other',
    type: 'input',
    inputKey: 'condicoes_clinicas_outras',
    content: 'Quais outras condições?',
    condition: hasSelection('condicoes_clinicas', 'outros'),
    validate: validateTextoLivre,
  },
  {
    id: 'hormonal_issues',
    type: 'choice',
    inputKey: 'problemas_hormonais',
    content: 'Você já recebeu diagnóstico de problemas hormonais?',
    options: [
      { label: 'Sim', value: 'sim' },
      { label: 'Não', value: 'nao' },
    ],
  },
  {
    id: 'hormonal_conditions_female',
    type: 'choice',
    inputKey: 'condicoes_hormonais',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Não sei especificar',
    content: 'Quais condições hormonais você tem?',
    condition: (ctx) => isFemale(ctx) && isYes('problemas_hormonais')(ctx),
    options: [
      { label: 'Hipotireoidismo', value: 'hipotireoidismo' },
      { label: 'Hipertireoidismo', value: 'hipertireoidismo' },
      { label: 'Resistência à insulina', value: 'resistencia_insulina' },
      { label: 'Síndrome do ovário policístico (SOP)', value: 'sop' },
      { label: 'Menopausa', value: 'menopausa' },
      { label: 'Alterações de cortisol', value: 'cortisol' },
      { label: 'Outra', value: 'outros' },
    ],
  },
  {
    id: 'hormonal_conditions_other',
    type: 'choice',
    inputKey: 'condicoes_hormonais',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Não sei especificar',
    content: 'Quais condições hormonais você tem?',
    condition: (ctx) => !isFemale(ctx) && isYes('problemas_hormonais')(ctx),
    options: [
      { label: 'Hipotireoidismo', value: 'hipotireoidismo' },
      { label: 'Hipertireoidismo', value: 'hipertireoidismo' },
      { label: 'Resistência à insulina', value: 'resistencia_insulina' },
      { label: 'Baixa testosterona', value: 'baixa_testosterona' },
      { label: 'Alterações de cortisol', value: 'cortisol' },
      { label: 'Outra', value: 'outros' },
    ],
  },
  {
    id: 'hormonal_conditions_other_text',
    type: 'input',
    inputKey: 'condicoes_hormonais_outros',
    content: 'Qual condição hormonal é essa?',
    condition: (ctx) =>
      isYes('problemas_hormonais')(ctx) && hasSelection('condicoes_hormonais', 'outros')(ctx),
    validate: validateTextoLivre,
  },
  {
    id: 'menstrual_cycle',
    type: 'choice',
    inputKey: 'ciclo_menstrual',
    content: 'Como é seu ciclo menstrual?',
    condition: isFemale,
    options: [
      { label: 'Regular', value: 'regular' },
      { label: 'Irregular', value: 'irregular' },
      { label: 'Não menstruo (DIU/hormonal)', value: 'nao_menstruo' },
      { label: 'Menopausa', value: 'menopausa' },
    ],
  },
  {
    id: 'hormonal_use',
    type: 'choice',
    inputKey: 'uso_hormonios',
    content: 'Você usa anticoncepcional ou terapia hormonal?',
    condition: isFemale,
    options: [
      { label: 'Sim', value: 'sim' },
      { label: 'Não', value: 'nao' },
    ],
  },
  {
    id: 'hormonal_type',
    type: 'choice',
    inputKey: 'tipo_hormonios',
    multiple: true,
    minSelections: 1,
    content: 'Qual tipo?',
    condition: (ctx) => isFemale(ctx) && isYes('uso_hormonios')(ctx),
    options: [
      { label: 'Pílula', value: 'pilula' },
      { label: 'Injetável', value: 'injetavel' },
      { label: 'DIU hormonal', value: 'diu_hormonal' },
      { label: 'Implante', value: 'implante' },
      { label: 'Reposição hormonal', value: 'reposicao' },
      { label: 'Outro', value: 'outros' },
    ],
  },
  {
    id: 'hormonal_type_other',
    type: 'input',
    inputKey: 'tipo_hormonios_outros',
    content: 'Qual é esse tipo?',
    condition: (ctx) =>
      isFemale(ctx) && isYes('uso_hormonios')(ctx) && hasSelection('tipo_hormonios', 'outros')(ctx),
    validate: validateTextoLivre,
  },

  // ========== MEDICATIONS & SURGERY ==========
  {
    id: 'intro_medication_surgery',
    type: 'message',
    content: 'Por fim, vamos falar sobre medicações e cirurgias.',
  },
  {
    id: 'weight_loss_medication',
    type: 'choice',
    inputKey: 'usa_medicacao_emagrecer',
    content: 'Você já usou medicação ou usa medicamento para emagrecer?',
    options: [
      { label: 'Sim', value: 'sim' },
      { label: 'Não', value: 'nao' },
    ],
  },
  {
    id: 'weight_loss_medication_types',
    type: 'choice',
    inputKey: 'medicacao_emagrecer_tipos',
    multiple: true,
    minSelections: 1,
    content: 'Quais medicações?',
    condition: isYes('usa_medicacao_emagrecer'),
    options: [
      { label: 'Semaglutida (Ozempic/Wegovy)', value: 'semaglutida' },
      { label: 'Liraglutida (Saxenda)', value: 'liraglutida' },
      { label: 'Tirzepatida (Mounjaro/Zepbound)', value: 'tirzepatida' },
      { label: 'Orlistat', value: 'orlistat' },
      { label: 'Sibutramina', value: 'sibutramina' },
      { label: 'Bupropiona + Naltrexona', value: 'bupropiona_naltrexona' },
      { label: 'Fitoterápicos', value: 'fitoterapicos' },
      { label: 'Outra', value: 'outros' },
    ],
  },
  {
    id: 'weight_loss_medication_duration',
    type: 'choice',
    inputKey: 'medicacao_emagrecer_duracao',
    content: 'Há quanto tempo você usa ou usou essas medicações?',
    condition: (ctx) =>
      isYes('usa_medicacao_emagrecer')(ctx) &&
      Array.isArray(ctx.vars.medicacao_emagrecer_tipos) &&
      ctx.vars.medicacao_emagrecer_tipos.length > 0,
    options: [
      { label: 'Ainda estou utilizando', value: 'ainda_utiliza' },
      { label: 'Até 6 meses', value: 'ate_6_meses' },
      { label: '6 meses a 1 ano', value: '6_meses_1_ano' },
      { label: 'Mais de 1 ano', value: 'mais_1_ano' },
    ],
  },
  {
    id: 'weight_loss_medication_other',
    type: 'input',
    inputKey: 'medicacao_emagrecer_outros',
    content: 'Qual medicação foi essa?',
    condition: (ctx) =>
      isYes('usa_medicacao_emagrecer')(ctx) &&
      hasSelection('medicacao_emagrecer_tipos', 'outros')(ctx),
    validate: validateTextoLivre,
  },
  {
    id: 'medication_side_effects',
    type: 'choice',
    inputKey: 'efeitos_colaterais',
    content: 'Você teve efeitos colaterais com essas medicações?',
    condition: isYes('usa_medicacao_emagrecer'),
    options: [
      { label: 'Sim', value: 'sim' },
      { label: 'Não', value: 'nao' },
      { label: 'Não lembro', value: 'nao_lembro' },
    ],
  },
  {
    id: 'medication_side_effects_detail',
    type: 'input',
    inputKey: 'efeitos_colaterais_quais',
    content: 'Quais efeitos colaterais você sentiu?',
    condition: (ctx) =>
      isYes('usa_medicacao_emagrecer')(ctx) && ctx.vars.efeitos_colaterais === 'sim',
    validate: validateTextoLivre,
  },
  {
    id: 'bariatric_surgery',
    type: 'choice',
    inputKey: 'fez_cirurgia_bariatrica',
    content: 'Você já fez alguma cirurgia bariátrica ou procedimento para emagrecimento?',
    options: [
      { label: 'Sim', value: 'sim' },
      { label: 'Não', value: 'nao' },
    ],
  },
  {
    id: 'bariatric_type',
    type: 'choice',
    inputKey: 'tipo_cirurgia_bariatrica',
    content: 'Qual procedimento você realizou?',
    condition: isYes('fez_cirurgia_bariatrica'),
    options: [
      { label: 'Bypass gástrico', value: 'bypass' },
      { label: 'Sleeve / Gastrectomia vertical', value: 'sleeve' },
      { label: 'Balão intragástrico', value: 'balao' },
      { label: 'Gastroplastia endoscópica', value: 'endoscopica' },
      { label: 'Outro', value: 'outro' },
    ],
  },
  {
    id: 'bariatric_type_other',
    type: 'input',
    inputKey: 'tipo_cirurgia_bariatrica_outro',
    content: 'Qual procedimento foi esse?',
    condition: (ctx) =>
      isYes('fez_cirurgia_bariatrica')(ctx) &&
      hasSelection('tipo_cirurgia_bariatrica', 'outro')(ctx),
    validate: validateTextoLivre,
  },
  {
    id: 'bariatric_time',
    type: 'choice',
    inputKey: 'tempo_cirurgia_bariatrica',
    content: 'Há quanto tempo foi o procedimento?',
    condition: isYes('fez_cirurgia_bariatrica'),
    options: [
      { label: 'Menos de 1 ano', value: 'menos_1_ano' },
      { label: '1 a 3 anos', value: '1_3_anos' },
      { label: '3 a 5 anos', value: '3_5_anos' },
      { label: 'Mais de 5 anos', value: 'mais_5_anos' },
    ],
  },
  {
    id: 'weight_regain',
    type: 'choice',
    inputKey: 'reganho_peso',
    content: 'Após o procedimento, houve reganho de peso?',
    condition: isYes('fez_cirurgia_bariatrica'),
    options: [
      { label: 'Sim', value: 'sim' },
      { label: 'Não', value: 'nao' },
    ],
  },
  {
    id: 'weight_regain_amount',
    type: 'choice',
    inputKey: 'reganho_peso_quanto',
    content: 'Aproximadamente quanto de peso foi reganho?',
    condition: (ctx) => isYes('fez_cirurgia_bariatrica')(ctx) && ctx.vars.reganho_peso === 'sim',
    options: [
      { label: 'Até 5 kg', value: 'ate_5' },
      { label: 'Entre 5 e 10 kg', value: '5_10' },
      { label: 'Entre 10 e 20 kg', value: '10_20' },
      { label: 'Mais de 20 kg', value: 'mais_20' },
    ],
  },

  // ========== NUTRITIONAL SIGNS ==========
  {
    id: 'intro_nutritional_signs',
    type: 'message',
    content: 'Alguns sinais podem indicar carências nutricionais.',
  },
  {
    id: 'nutritional_signs',
    type: 'choice',
    inputKey: 'sinais_nutricionais',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Nenhum desses',
    content: 'Você percebe algum desses sinais?',
    options: [
      { label: 'Queda de cabelo', value: 'queda_cabelo' },
      { label: 'Unhas fracas ou quebradiças', value: 'unhas_fracas' },
      { label: 'Pele seca ou opaca', value: 'pele_seca' },
      { label: 'Feridas que demoram a cicatrizar', value: 'cicatrizacao_lenta' },
      { label: 'Cansaço frequente', value: 'cansaco' },
    ],
  },

  // ========== CLOSING ==========
  {
    id: 'closing_weight_loss',
    type: 'message',
    content:
      'Obrigado! Com essas informações, vamos montar um protocolo mais inteligente para você.',
  },
]
